import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import BriefSentence from "@/components/offsite-demo/BriefSentence";
import HeroPoster from "@/components/offsite-demo/HeroPoster";
import Closing from "@/components/offsite-demo/Closing";
import DemoMenu from "@/components/offsite-demo/DemoMenu";
import { useOffsite } from "./OffsiteLayout";

export default function OffsiteDemo() {
  const { brief, setBrief, reset } = useOffsite();
  const [thinking, setThinking] = useState(false);
  const navigate = useNavigate();
  const timer = useRef<number>();

  useEffect(() => {
    document.title = "qwip offsites | your personal team day out planner";
    return () => window.clearTimeout(timer.current);
  }, []);

  const scrollTo = (id: string) => {
    if (id === "plans") {
      navigate("/offsite-demo/plans");
      return;
    }
    window.setTimeout(
      () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }),
      80
    );
  };

  const plan = () => {
    if (thinking) return;
    setThinking(true);
    timer.current = window.setTimeout(() => {
      setThinking(false);
      navigate("/offsite-demo/plans");
    }, 1400);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="absolute top-0 left-0 right-0 z-40 w-full">
        <div className="container mx-auto px-4 laptop:px-6 desktop:px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-baseline gap-2"
          >
            <span className="font-funnel font-bold text-2xl text-primary-foreground mix-blend-difference">
              qwip
            </span>
            <span className="text-sm text-primary-foreground/80 mix-blend-difference">offsites</span>
          </button>
          <button
            onClick={() => scrollTo("brief")}
            className="rounded-full border border-primary-foreground/40 text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary-foreground/10 transition-colors mix-blend-difference"
          >
            what do you have for me
          </button>
        </div>
      </header>

      <HeroPoster onScrollToBrief={() => scrollTo("brief")} />

      <BriefSentence brief={brief} setBrief={setBrief} onPlan={plan} thinking={thinking} />

      <AnimatePresence>
        {thinking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/95 backdrop-blur flex items-center justify-center"
          >
            <div className="container mx-auto px-4 laptop:px-6 desktop:px-6 space-y-4">
              {[
                "balancing travel across your cities",
                "matching venues to your budget",
                "building three complete days",
              ].map((line, i) => (
                <motion.p
                  key={line}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.35 }}
                  className="font-funnel text-xl tablet:text-2xl text-foreground"
                >
                  {line}...
                </motion.p>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Closing onPlan={() => scrollTo("brief")} />

      <DemoMenu
        onGo={scrollTo}
        onReset={() => {
          reset();
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
    </div>
  );
}
